import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Icon } from '@inter-ikea-kompis/component-icon';

import IconPillSizeOptionsEnum from '../../enums/IconPillSizeOptionsEnum.js';
import IconPillStyle from './IconPillStyle.js';

/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * Icon Pill class.
 *
 * @event select Icon pill triggers props select event.
 */

export default class IconPill extends KompisElement {
	// Select
	@property({ type: Boolean })
	public selected = false;

	// Icon in the pill
	@property({ type: String })
	public icon: string | null = null;

	// Disabled mode
	@property({ type: Boolean })
	public disabled = false;

	// Icon pill size option
	@property({ type: String })
	public iconPillSize: IconPillSizeOptionsEnum = IconPillSizeOptionsEnum.medium;

	// Label for the icon pill to set as an aria-label for accessibility reasons
	@property({ type: String })
	public override ariaLabel: string | null = null;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	private hasFocus = false;
	private pointerIsDown = false;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${IconPillStyle.getStyle({
					hasFocus: this.hasFocus,
					iconPillSize: this.iconPillSize,
					selected: this.selected,
					isDisabled: this.disabled,
					theme: this.theme
				})}
			</style>
			<div
				class="iconPillButton"
				tabindex="0"
				role="checkbox"
				aria-label="${this.ariaLabel}"
				aria-checked="${this.selected}"
				aria-disabled="${this.disabled}"
				@focusout="${this.onFocusOut.bind(this)}"
				@focusin="${this.onFocus.bind(this)}"
				@click="${this.onChange.bind(this)}"
				@keydown="${this.onKeyDown.bind(this)}"
				@pointerdown="${this.onPointerDown.bind(this)}"
			>
				<div class="outline"></div>
				${this.getIcon()}
			</div>
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-icon', Icon);

		super.connectedCallback();
	}

	/**
	 * Get the icon.
	 *
	 * @returns Template.
	 */
	private getIcon(): RenderResult {
		if (!this.icon) {
			return null;
		}

		return html` <kompis-icon .theme="${this.theme}" .icon="${this.icon}"></kompis-icon> `;
	}

	/**
	 * Event handler.
	 *
	 * @param event HTMLEvent.
	 */
	private onChange(event: Event): void {
		event.stopPropagation();
		event.preventDefault();

		if (this.disabled) {
			return;
		}

		this.dispatchEvent(
			new CustomEvent('select', {
				bubbles: true,
				detail: {
					selected: !this.selected
				}
			})
		);
		this.pointerIsDown = false;
	}

	/**
	 * Event handler.
	 *
	 * @param event HTMLEvent.
	 */
	private onKeyDown(event: KeyboardEvent): void {
		if (this.disabled) {
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			this.onChange(event);
		}
	}

	/**
	 * HTML event handler.
	 */
	private onFocusOut(): void {
		if (this.hasFocus) {
			this.hasFocus = false;
			this.pointerIsDown = false;
			this.update();
		}
	}

	/**
	 * HTML event handler.
	 */
	private onFocus(): void {
		if (this.pointerIsDown) {
			return;
		}
		this.hasFocus = true;
		this.update();
	}

	/**
	 * HTML event handler.
	 *
	 */
	private onPointerDown(): void {
		this.pointerIsDown = true;
		if (this.hasFocus) {
			this.hasFocus = false;
			this.update();
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-icon-pill': IconPill;
	}
}
